import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { Button, Drawer, DrawerFooter, DrawerHeader, Field, Input } from '@/design-system';
import { clientApi, type Client, type ClientChanges } from '@/entities/client';
import { orderKeys } from '@/entities/order';
import { reportKeys } from '@/entities/report';
import { errorMessage, isApiError } from '@/shared/api';
import { ClientAddressFields, useAddressLookup, type AddressFieldName } from '@/shared/ui';

import { dossierCopy } from '../copy';

export interface EditClientPanelProps {
    client: Client;
    open: boolean;
    onClose: () => void;
    /** Na opslaan: het dossier zegt dat het gelukt is. */
    onSaved: (client: Client) => void;
}

type ContactField = 'name' | 'contactName' | 'email' | 'phone';
type ContactValues = Pick<ClientChanges, ContactField>;

/** De velden van het formulier onder de namen die de API in een 422 gebruikt. */
const API_FIELDS: Record<ContactField | AddressFieldName, string> = {
    name: 'name',
    contactName: 'contact_name',
    email: 'email',
    phone: 'phone',
    street: 'address_street',
    houseNumber: 'address_number',
    suffix: 'address_suffix',
    postcode: 'address_postcode',
    city: 'address_city',
};

function contactFor(client: Client): ContactValues {
    return {
        name: client.name,
        contactName: client.contactName ?? '',
        email: client.email ?? '',
        phone: client.phone ?? '',
    };
}

/**
 * "Klant bewerken" (scherm 8): naam, contactpersoon, e-mailadres, telefoon en
 * adres van de klant van deze keuring, via `PATCH /clients/{client}`. Het adres
 * zoekt op zoals bij een nieuwe keuring ({@link ClientAddressFields}): het
 * bewaarde adres blijft staan tot postcode of huisnummer verandert.
 *
 * Hier kom je ook vanuit het rapport als er geen ontvanger met een e-mailadres
 * is. Een rapport dat nog niet verstuurd is neemt het nieuwe adres over; daarom
 * leest het rapport na opslaan opnieuw, net als het dossier en de werklijst.
 */
export function EditClientPanel({ client, open, onClose, onSaved }: EditClientPanelProps) {
    const copy = dossierCopy.editClient;

    return (
        <Drawer open={open} onClose={onClose} side="right" width={480} title={copy.title}>
            <DrawerHeader
                icon="edit"
                title={copy.title}
                description={copy.description}
                onClose={onClose}
                closeLabel={copy.close}
            />
            {/* Een dichte Drawer rendert niets: elk openen begint met de klant zoals hij nu is. */}
            <EditClientForm client={client} onCancel={onClose} onSaved={onSaved} />
        </Drawer>
    );
}

function EditClientForm({
    client,
    onCancel,
    onSaved,
}: {
    client: Client;
    onCancel: () => void;
    onSaved: (client: Client) => void;
}) {
    const copy = dossierCopy.editClient;
    const queryClient = useQueryClient();
    const [values, setValues] = useState<ContactValues>(() => contactFor(client));
    const home = useAddressLookup(client.address);
    const [nameMissing, setNameMissing] = useState(false);
    const [homeIncomplete, setHomeIncomplete] = useState(false);

    const save = useMutation({
        mutationFn: (changes: ClientChanges) => clientApi.update(client.uuid, changes),
        onSuccess: (saved) => {
            void queryClient.invalidateQueries({ queryKey: orderKeys.all });
            void queryClient.invalidateQueries({ queryKey: reportKeys.all });
            void queryClient.invalidateQueries({ queryKey: ['clients'] });
            onSaved(saved);
        },
    });

    const fieldError = (field: ContactField | AddressFieldName): string | undefined => {
        if (field === 'name' && nameMissing) {
            return copy.nameMissing;
        }

        return isApiError(save.error)
            ? save.error.firstValidationError(API_FIELDS[field])
            : undefined;
    };

    const set = (field: ContactField) => (event: { target: { value: string } }) => {
        const value = event.target.value;

        setValues((current) => ({ ...current, [field]: value }));

        if (field === 'name') {
            setNameMissing(false);
        }
    };

    const submit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const address = home.resolved;

        setNameMissing(values.name.trim() === '');
        setHomeIncomplete(address === null);

        if (values.name.trim() === '' || address === null) {
            return;
        }

        save.mutate({ ...values, address });
    };

    const generalProblem =
        save.error && !(isApiError(save.error) && save.error.kind === 'validation')
            ? errorMessage(save.error)
            : null;

    return (
        <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col pt-5" noValidate>
            <div className="mx-5 mb-6 grid gap-3">
                <Field label={copy.name} error={fieldError('name')}>
                    <Input value={values.name} autoComplete="off" onChange={set('name')} required />
                </Field>
                <Field
                    label={copy.contactName}
                    description={copy.contactNameHint}
                    error={fieldError('contactName')}
                >
                    <Input
                        value={values.contactName}
                        autoComplete="off"
                        onChange={set('contactName')}
                    />
                </Field>
                <Field label={copy.email} description={copy.emailHint} error={fieldError('email')}>
                    <Input
                        type="email"
                        inputMode="email"
                        value={values.email}
                        autoComplete="off"
                        onChange={set('email')}
                    />
                </Field>
                <Field label={copy.phone} description={copy.phoneHint} error={fieldError('phone')}>
                    <Input
                        type="tel"
                        inputMode="tel"
                        value={values.phone}
                        autoComplete="off"
                        onChange={set('phone')}
                    />
                </Field>
            </div>

            <fieldset className="m-0 mx-5 mb-6 grid min-w-0 gap-3 border-0 border-t border-[var(--border-subtle)] p-0 pt-6">
                <legend className="float-left mb-1 w-full p-0 font-[family-name:var(--font-display)] text-[16px] leading-[1.3] font-semibold text-[var(--text-primary)]">
                    {copy.addressSection}
                </legend>
                <p className="m-0 text-[12px] leading-[1.4] text-[var(--text-tertiary-safe)]">
                    {copy.addressHint}
                </p>
                <ClientAddressFields
                    address={home}
                    errors={{
                        postcode: fieldError('postcode'),
                        houseNumber: fieldError('houseNumber'),
                        suffix: fieldError('suffix'),
                        street: fieldError('street'),
                        city: fieldError('city'),
                    }}
                />
                {homeIncomplete && home.resolved === null ? (
                    <p role="alert" className="m-0 text-[12px] leading-[1.4] text-[var(--danger)]">
                        {copy.addressIncomplete}
                    </p>
                ) : null}
            </fieldset>

            {generalProblem ? (
                <p
                    role="alert"
                    className="m-0 mx-5 mb-4 text-[13px] leading-[1.5] text-[var(--danger)]"
                >
                    {generalProblem}
                </p>
            ) : null}

            <DrawerFooter>
                <Button type="submit" variant="primary" size="md" disabled={save.isPending}>
                    {save.isPending ? copy.saving : copy.save}
                </Button>
                <Button variant="outline" size="md" disabled={save.isPending} onClick={onCancel}>
                    {copy.cancel}
                </Button>
            </DrawerFooter>
        </form>
    );
}
